import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Alerta from "./Alertas";

import {
  db,
  auth,
  firebaseConfig,
} from "./../firebase/firebaseConfig";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";

import {
  getApp,
  getApps,
  initializeApp,
} from "firebase/app";

import {
  createUserWithEmailAndPassword,
  deleteUser,
  getAuth,
  signOut,
} from "firebase/auth";

import {
  RolesHomeCare,
  RolesST,
} from "./Roles";

import { UserContext } from "../context/UserContext";

import ExportarExcel from "../funciones/ExportarExcel";

import {
  Button,
  Checkbox,
  Container,
  Dropdown,
  Form,
  Header,
  Icon,
  Input,
  Label,
  Message,
  Modal,
  Segment,
  Table,
} from "semantic-ui-react";

// ============================================================
// SECONDARY AUTH
// ============================================================

const secondaryApp =
  getApps().some((app) => app.name === "Secondary")
    ? getApp("Secondary")
    : initializeApp(firebaseConfig, "Secondary");

const secondaryAuth = getAuth(secondaryApp);

// ============================================================
// CONSTANTES
// ============================================================

const ROLES_HC = [
  "ADMIN-HC",
  "SUPERVISOR-HC",
  "TRANSPORTE-HC",
];

const ROLES_ST = [
  "ADMIN-LMST",
  "TECNICO-LMST",
];

const ROLES_BACKOFFICE = [
  "DADMIN",
  "JADMIN",
];

const DOMINIOS = {
  HC: "HOMECARE",
  ST: "SERVICIO_TECNICO",
  TODOS: "TODOS",
};

// ============================================================
// HELPERS
// ============================================================

const normalizarCorreo = (valor) =>
  String(valor || "")
    .trim()
    .toLowerCase();

const esRolHC = (rol) =>
  ROLES_HC.includes(rol);

const esRolST = (rol) =>
  ROLES_ST.includes(rol);

const obtenerDominioRol = (rol) => {
  if (esRolHC(rol)) {
    return DOMINIOS.HC;
  }

  if (esRolST(rol)) {
    return DOMINIOS.ST;
  }

  if (ROLES_BACKOFFICE.includes(rol)) {
    return "BACKOFFICE";
  }

  return "DESCONOCIDO";
};

// ============================================================
// COMPONENTE
// ============================================================

export const RegistroUsuarios = () => {
  const { users } = useContext(UserContext);

  // ==========================================================
  // REFERENCIA ADMINISTRADOR
  // ==========================================================

  const adminRef = useRef({
    uid: "",
    email: "",
    rol: "",
    emp_id: "",
  });

  // ==========================================================
  // FORMULARIO CREACIÓN
  // ==========================================================

  const [correo, setCorreo] = useState("");
  const [pass, setPass] = useState("");
  const [pass2, setPass2] = useState("");
  const [nombre, setNombre] = useState("");
  const [apellido, setApellido] = useState("");
  const [rol, setRol] = useState("");

  // ==========================================================
  // DATOS
  // ==========================================================

  const [usuarioActual, setUsuarioActual] =
    useState(null);

  const [empresas, setEmpresas] =
    useState([]);

  const [
    empresaSeleccionadaId,
    setEmpresaSeleccionadaId,
  ] = useState("");

  const [usuarios, setUsuarios] =
    useState([]);

  const [maxUsers, setMaxUsers] =
    useState(null);

  // ==========================================================
  // UI
  // ==========================================================

  const [alerta, cambiarAlerta] =
    useState({});

  const [
    estadoAlerta,
    cambiarEstadoAlerta,
  ] = useState(false);

  const [buscador, setBuscador] =
    useState("");

  const [
    buscadorDebounced,
    setBuscadorDebounced,
  ] = useState("");

  const [
    filtroDominio,
    setFiltroDominio,
  ] = useState(DOMINIOS.TODOS);

  const [
    confirmOpen,
    setConfirmOpen,
  ] = useState(false);

  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false);

  const [
    loadingInicial,
    setLoadingInicial,
  ] = useState(true);

  const [
    selectedUserId,
    setSelectedUserId,
  ] = useState(null);

  const [
    savingById,
    setSavingById,
  ] = useState({});

  const [flag, setFlag] =
    useState(false);

  // ==========================================================
  // CAMBIO DE ROL
  // ==========================================================

  const [
    modalRolOpen,
    setModalRolOpen,
  ] = useState(false);

  const [
    usuarioCambioRol,
    setUsuarioCambioRol,
  ] = useState(null);

  const [
    nuevoRol,
    setNuevoRol,
  ] = useState("");

  const [
    guardandoRol,
    setGuardandoRol,
  ] = useState(false);

  // ==========================================================
  // USUARIO CONECTADO
  // ==========================================================

  const rolActual =
    usuarioActual?.rol ||
    users?.rol ||
    "";

  const esBackoffice =
    rolActual === "DADMIN" ||
    rolActual === "JADMIN";

  const esAdminHC =
    rolActual === "ADMIN-HC";

  const esAdminST =
    rolActual === "ADMIN-LMST";

  const puedeAdministrar =
    esAdminHC ||
    esAdminST;

  const dominioAdministrador =
    useMemo(() => {
      if (esAdminHC) {
        return DOMINIOS.HC;
      }

      if (esAdminST) {
        return DOMINIOS.ST;
      }

      if (esBackoffice) {
        return DOMINIOS.TODOS;
      }

      return "";
    }, [
      esAdminHC,
      esAdminST,
      esBackoffice,
    ]);

  // ==========================================================
  // TÍTULOS
  // ==========================================================

  const tituloPagina =
    useMemo(() => {
      if (esAdminHC) {
        return "Usuarios HomeCare";
      }

      if (esAdminST) {
        return "Usuarios Servicio Técnico";
      }

      if (esBackoffice) {
        return "Usuarios y Roles";
      }

      return "Usuarios";
    }, [
      esAdminHC,
      esAdminST,
      esBackoffice,
    ]);

  const subtituloPagina =
    useMemo(() => {
      if (esAdminHC) {
        return "Administración de usuarios y roles del dominio HomeCare.";
      }

      if (esAdminST) {
        return "Administración de usuarios de LogisticMed ST.";
      }

      if (esBackoffice) {
        return "Consulta de usuarios HomeCare y Servicio Técnico.";
      }

      return "Administración de usuarios.";
    }, [
      esAdminHC,
      esAdminST,
      esBackoffice,
    ]);

  // ==========================================================
  // DEBOUNCE
  // ==========================================================

  useEffect(() => {
    const timer =
      setTimeout(() => {
        setBuscadorDebounced(
          buscador
        );
      }, 250);

    return () =>
      clearTimeout(timer);
  }, [buscador]);

  // ==========================================================
  // ALERTA
  // ==========================================================

  const mostrarAlerta = (
    tipo,
    mensaje
  ) => {
    cambiarEstadoAlerta(true);

    cambiarAlerta({
      tipo,
      mensaje,
    });
  };

  // ==========================================================
  // EMPRESA ACTUAL
  // ==========================================================

  const empresaActual =
    useMemo(() => {
      return (
        empresas.find(
          (e) =>
            e.id ===
            empresaSeleccionadaId
        ) || null
      );
    }, [
      empresas,
      empresaSeleccionadaId,
    ]);

  // ==========================================================
  // CARGAR DATOS
  // ==========================================================

  const cargarDatos = async () => {
    const currentUser =
      auth.currentUser;

    if (!currentUser?.uid) {
      setLoadingInicial(false);
      return;
    }

    setLoadingInicial(true);
    cambiarEstadoAlerta(false);

    try {
      // ------------------------------------------------------
      // USUARIO AUTENTICADO
      // ------------------------------------------------------

      const usuarioRef = doc(
        db,
        "usuarios",
        currentUser.uid
      );

      const usuarioSnap =
        await getDoc(usuarioRef);

      if (!usuarioSnap.exists()) {
        mostrarAlerta(
          "error",
          "No se encontró el usuario autenticado en Firestore."
        );

        return;
      }

      const actual = {
        id: usuarioSnap.id,
        ...usuarioSnap.data(),
      };

      setUsuarioActual(actual);

      adminRef.current = {
        uid: actual.id,

        email: normalizarCorreo(
          actual.correo ||
          currentUser.email
        ),

        rol: actual.rol || "",

        emp_id:
          actual.emp_id || "",
      };

      const global =
        actual.rol === "DADMIN" ||
        actual.rol === "JADMIN";

      // ------------------------------------------------------
      // BACKOFFICE
      // ------------------------------------------------------

      if (global) {
        const snapEmpresas =
          await getDocs(
            collection(
              db,
              "empresas"
            )
          );

        const arrEmpresas =
          snapEmpresas.docs
            .map((d) => ({
              id: d.id,
              ...d.data(),
            }))
            .sort((a, b) =>
              String(
                a.empresa || ""
              ).localeCompare(
                String(
                  b.empresa || ""
                )
              )
            );

        setEmpresas(
          arrEmpresas
        );

        setEmpresaSeleccionadaId(
          (actualId) => {
            if (
              actualId &&
              arrEmpresas.some(
                (e) =>
                  e.id ===
                  actualId
              )
            ) {
              return actualId;
            }

            return (
              arrEmpresas[0]?.id ||
              ""
            );
          }
        );

        const snapUsuarios =
          await getDocs(
            collection(
              db,
              "usuarios"
            )
          );

        const arrUsuarios =
          snapUsuarios.docs.map(
            (d) => ({
              id: d.id,
              ...d.data(),
            })
          );

        setUsuarios(
          arrUsuarios
        );

        return;
      }

      // ------------------------------------------------------
      // ADMIN TENANT
      // ------------------------------------------------------

      if (!actual.emp_id) {
        mostrarAlerta(
          "error",
          "El usuario no tiene empresa asignada."
        );

        return;
      }

      const empresaRef = doc(
        db,
        "empresas",
        actual.emp_id
      );

      const empresaSnap =
        await getDoc(
          empresaRef
        );

      if (!empresaSnap.exists()) {
        mostrarAlerta(
          "error",
          "No se encontró la empresa asociada al usuario."
        );

        return;
      }

      const empresaData = {
        id: empresaSnap.id,
        ...empresaSnap.data(),
      };

      setEmpresas([
        empresaData,
      ]);

      setEmpresaSeleccionadaId(
        actual.emp_id
      );

      const qUsuarios = query(
        collection(
          db,
          "usuarios"
        ),
        where(
          "emp_id",
          "==",
          actual.emp_id
        )
      );

      const snapUsuarios =
        await getDocs(
          qUsuarios
        );

      const arrUsuarios =
        snapUsuarios.docs.map(
          (d) => ({
            id: d.id,
            ...d.data(),
          })
        );

      setUsuarios(
        arrUsuarios
      );
    } catch (error) {
      console.error(
        "Error cargando usuarios:",
        error
      );

      mostrarAlerta(
        "error",
        "No fue posible cargar la administración de usuarios."
      );
    } finally {
      setLoadingInicial(false);
    }
  };

  useEffect(() => {
    cargarDatos();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flag]);

  // ==========================================================
  // MAX USUARIOS
  // ==========================================================

  useEffect(() => {
    if (!empresaActual) {
      setMaxUsers(null);
      return;
    }

    const limite = Number(
      empresaActual.maxUsers ||
      empresaActual.maxusuarios ||
      empresaActual.max_usuarios ||
      0
    );

    setMaxUsers(
      limite > 0
        ? limite
        : null
    );
  }, [empresaActual]);

  // ==========================================================
  // ROLES DISPONIBLES
  // ==========================================================

  const rolesDisponibles =
    useMemo(() => {
      if (esAdminHC) {
        return RolesHomeCare;
      }

      if (esAdminST) {
        return RolesST;
      }

      return [];
    }, [
      esAdminHC,
      esAdminST,
    ]);

  // ==========================================================
  // USUARIOS EMPRESA
  // ==========================================================

  const usuariosEmpresa =
    useMemo(() => {
      if (
        !empresaSeleccionadaId
      ) {
        return [];
      }

      return usuarios.filter(
        (u) =>
          u.emp_id ===
          empresaSeleccionadaId
      );
    }, [
      usuarios,
      empresaSeleccionadaId,
    ]);

  // ==========================================================
  // USUARIOS DOMINIO
  // ==========================================================

  const usuariosDominio =
    useMemo(() => {
      let resultado =
        usuariosEmpresa;

      if (esAdminHC) {
        resultado =
          resultado.filter(
            (u) =>
              esRolHC(u.rol)
          );
      } else if (esAdminST) {
        resultado =
          resultado.filter(
            (u) =>
              esRolST(u.rol)
          );
      } else if (esBackoffice) {
        resultado =
          resultado.filter(
            (u) =>
              esRolHC(u.rol) ||
              esRolST(u.rol)
          );

        if (
          filtroDominio ===
          DOMINIOS.HC
        ) {
          resultado =
            resultado.filter(
              (u) =>
                esRolHC(u.rol)
            );
        }

        if (
          filtroDominio ===
          DOMINIOS.ST
        ) {
          resultado =
            resultado.filter(
              (u) =>
                esRolST(u.rol)
            );
        }
      } else {
        resultado = [];
      }

      return [...resultado].sort(
        (a, b) => {
          const rolA =
            String(
              a.rol || ""
            );

          const rolB =
            String(
              b.rol || ""
            );

          const rolComp =
            rolA.localeCompare(
              rolB
            );

          if (rolComp !== 0) {
            return rolComp;
          }

          return String(
            a.nombre || ""
          ).localeCompare(
            String(
              b.nombre || ""
            )
          );
        }
      );
    }, [
      usuariosEmpresa,
      esAdminHC,
      esAdminST,
      esBackoffice,
      filtroDominio,
    ]);

  // ==========================================================
  // PLAN
  // ==========================================================

  const usuariosActivosEmpresa =
    useMemo(() => {
      return usuariosEmpresa.filter(
        (u) =>
          u.activo !== false &&
          u.rol !== "DADMIN" &&
          u.rol !== "JADMIN"
      );
    }, [usuariosEmpresa]);

  const limiteUsuariosAlcanzado =
    useMemo(() => {
      if (!maxUsers) {
        return false;
      }

      return (
        usuariosActivosEmpresa.length >=
        maxUsers
      );
    }, [
      maxUsers,
      usuariosActivosEmpresa.length,
    ]);

  // ==========================================================
  // FORM
  // ==========================================================

  const limpiarFormulario = () => {
    setNombre("");
    setApellido("");
    setCorreo("");
    setPass("");
    setPass2("");
    setRol("");
  };

  const handleChange = (e) => {
    switch (e.target.name) {
      case "email":
        setCorreo(
          e.target.value
        );
        break;

      case "password":
        setPass(
          e.target.value
        );
        break;

      case "password2":
        setPass2(
          e.target.value
        );
        break;

      case "nombre":
        setNombre(
          e.target.value
        );
        break;

      case "apellido":
        setApellido(
          e.target.value
        );
        break;

      default:
        break;
    }
  };

  // ==========================================================
  // VALIDAR ROL SEGÚN ADMINISTRADOR
  // ==========================================================

  const rolPermitidoParaAdministrador =
    (rolNuevo) => {
      if (esAdminHC) {
        return esRolHC(
          rolNuevo
        );
      }

      if (esAdminST) {
        return esRolST(
          rolNuevo
        );
      }

      return false;
    };

  // ==========================================================
  // CREAR USUARIO - VALIDACIÓN INICIAL
  // ==========================================================

  const handleSubmit =
    async (e) => {
      e.preventDefault();

      cambiarEstadoAlerta(
        false
      );

      const expresionRegular =
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      const correoNormalizado =
        normalizarCorreo(
          correo
        );

      if (!puedeAdministrar) {
        mostrarAlerta(
          "error",
          "Tu perfil no puede crear usuarios desde esta pantalla."
        );

        return;
      }

      if (!empresaActual?.id) {
        mostrarAlerta(
          "error",
          "No se pudo identificar la empresa."
        );

        return;
      }

      if (
        limiteUsuariosAlcanzado
      ) {
        mostrarAlerta(
          "error",
          `El plan permite máximo ${maxUsers} usuario(s) activos.`
        );

        return;
      }

      if (!nombre.trim()) {
        mostrarAlerta(
          "error",
          "Campo Nombre no puede estar vacío."
        );

        return;
      }

      if (!apellido.trim()) {
        mostrarAlerta(
          "error",
          "Campo Apellido no puede estar vacío."
        );

        return;
      }

      if (
        !expresionRegular.test(
          correoNormalizado
        )
      ) {
        mostrarAlerta(
          "error",
          "Favor ingresa un correo válido."
        );

        return;
      }

      if (pass.length < 6) {
        mostrarAlerta(
          "error",
          "La contraseña debe tener al menos 6 caracteres."
        );

        return;
      }

      if (pass !== pass2) {
        mostrarAlerta(
          "error",
          "Las contraseñas no son iguales."
        );

        return;
      }

      if (!rol) {
        mostrarAlerta(
          "error",
          "Favor seleccionar rol."
        );

        return;
      }

      if (
        !rolPermitidoParaAdministrador(
          rol
        )
      ) {
        mostrarAlerta(
          "error",
          "El rol seleccionado no pertenece al dominio que administras."
        );

        return;
      }

      try {
        const qCorreo = query(
          collection(
            db,
            "usuarios"
          ),
          where(
            "correo",
            "==",
            correoNormalizado
          ),
          limit(1)
        );

        const snapCorreo =
          await getDocs(
            qCorreo
          );

        if (!snapCorreo.empty) {
          const existente =
            snapCorreo.docs[0].data();

          mostrarAlerta(
            "error",
            `El correo ya pertenece a un usuario${
              existente?.empresa
                ? ` de ${existente.empresa}`
                : ""
            }.`
          );

          return;
        }

        setConfirmOpen(true);
      } catch (error) {
        console.error(
          "Error verificando correo:",
          error
        );

        mostrarAlerta(
          "error",
          "No fue posible verificar si el correo ya existe."
        );
      }
    };

  // ==========================================================
  // CONFIRMAR CREACIÓN
  // ==========================================================

  const confirmCreateUser =
    async () => {
      if (isSubmitting) {
        return;
      }

      setIsSubmitting(true);
      cambiarEstadoAlerta(false);

      let usuarioAuthCreado =
        null;

      let firestoreCreado =
        false;

      try {
        if (!puedeAdministrar) {
          throw new Error(
            "Perfil sin autorización para crear usuarios."
          );
        }

        if (!empresaActual?.id) {
          throw new Error(
            "No se pudo identificar la empresa."
          );
        }

        if (
          !rolPermitidoParaAdministrador(
            rol
          )
        ) {
          throw new Error(
            "Rol no autorizado para este dominio."
          );
        }

        const correoNormalizado =
          normalizarCorreo(
            correo
          );

        // ----------------------------------------------------
        // REVISIÓN FRESCA PLAN
        // ----------------------------------------------------

        const qUsuariosEmpresa =
          query(
            collection(
              db,
              "usuarios"
            ),
            where(
              "emp_id",
              "==",
              empresaActual.id
            )
          );

        const snapUsuariosEmpresa =
          await getDocs(
            qUsuariosEmpresa
          );

        const activosActuales =
          snapUsuariosEmpresa.docs
            .map(
              (d) =>
                d.data()
            )
            .filter(
              (u) =>
                u.activo !==
                  false &&
                u.rol !==
                  "DADMIN" &&
                u.rol !==
                  "JADMIN"
            ).length;

        if (
          maxUsers &&
          activosActuales >=
            maxUsers
        ) {
          throw new Error(
            `El plan permite máximo ${maxUsers} usuario(s) activos.`
          );
        }

        // ----------------------------------------------------
        // REVISIÓN FRESCA CORREO
        // ----------------------------------------------------

        const qCorreo = query(
          collection(
            db,
            "usuarios"
          ),
          where(
            "correo",
            "==",
            correoNormalizado
          ),
          limit(1)
        );

        const snapCorreo =
          await getDocs(
            qCorreo
          );

        if (!snapCorreo.empty) {
          throw new Error(
            "Ya existe un usuario registrado con ese correo."
          );
        }

        // ----------------------------------------------------
        // AUTH SECUNDARIO
        // ----------------------------------------------------

        const cred =
          await createUserWithEmailAndPassword(
            secondaryAuth,
            correoNormalizado,
            pass
          );

        usuarioAuthCreado =
          cred.user;

        const nuevoUid =
          cred.user.uid;

        // ----------------------------------------------------
        // USUARIO + AUDITORÍA
        // ----------------------------------------------------

        const batch =
          writeBatch(db);

        const usuarioRef =
          doc(
            db,
            "usuarios",
            nuevoUid
          );

        batch.set(
          usuarioRef,
          {
            nombre:
              nombre.trim(),

            apellido:
              apellido.trim(),

            empresa:
              empresaActual.empresa ||
              "",

            emp_id:
              empresaActual.id,

            correo:
              correoNormalizado,

            rol,

            activo: true,

            origen:
              dominioAdministrador,

            es_admin_inicial:
              false,

            user_add:
              adminRef.current.email,

            user_mod:
              adminRef.current.email,

            fecha_add:
              serverTimestamp(),

            fecha_mod:
              serverTimestamp(),
          }
        );

        const auditoriaRef =
          doc(
            collection(
              db,
              "auditoria_usuarios"
            )
          );

        batch.set(
          auditoriaRef,
          {
            accion:
              "CREAR_USUARIO",

            modulo:
              "CONFIGURACION",

            dominio:
              dominioAdministrador,

            entidad:
              "USUARIO",

            emp_id:
              empresaActual.id,

            empresa:
              empresaActual.empresa ||
              "",

            objetivo_id:
              nuevoUid,

            objetivo_descripcion:
              correoNormalizado,

            rol_objetivo:
              rol,

            ejecutado_por_uid:
              adminRef.current.uid,

            ejecutado_por_correo:
              adminRef.current.email,

            ejecutado_por_rol:
              adminRef.current.rol,

            datos_anteriores:
              null,

            datos_nuevos: {
              nombre:
                nombre.trim(),

              apellido:
                apellido.trim(),

              correo:
                correoNormalizado,

              rol,

              activo: true,
            },

            resultado: "OK",

            fecha:
              serverTimestamp(),
          }
        );

        await batch.commit();

        firestoreCreado =
          true;

        try {
          await signOut(
            secondaryAuth
          );
        } catch (
          signOutError
        ) {
          console.warn(
            "Usuario creado correctamente, pero falló signOut secondaryAuth:",
            signOutError
          );
        }

        usuarioAuthCreado =
          null;

        limpiarFormulario();

        setConfirmOpen(
          false
        );

        mostrarAlerta(
          "exito",
          "Usuario registrado correctamente."
        );

        setFlag(
          (valor) =>
            !valor
        );
      } catch (error) {
        console.error(
          "Error creando usuario:",
          error
        );

        if (
          usuarioAuthCreado &&
          !firestoreCreado
        ) {
          try {
            await deleteUser(
              usuarioAuthCreado
            );
          } catch (
            rollbackError
          ) {
            console.error(
              "No fue posible hacer rollback del usuario Auth:",
              rollbackError
            );
          }
        }

        try {
          if (
            secondaryAuth.currentUser
          ) {
            await signOut(
              secondaryAuth
            );
          }
        } catch (
          signOutError
        ) {
          console.warn(
            signOutError
          );
        }

        let mensaje =
          error?.message ||
          "No fue posible crear el usuario.";

        switch (error?.code) {
          case "auth/weak-password":
            mensaje =
              "La contraseña debe tener al menos 6 caracteres.";
            break;

          case "auth/email-already-in-use":
            mensaje =
              "El correo ya está registrado en Firebase Authentication.";
            break;

          case "auth/invalid-email":
            mensaje =
              "El correo no es válido.";
            break;

          case "permission-denied":
          case "firestore/permission-denied":
            mensaje =
              "Las reglas de seguridad no permiten crear este usuario.";
            break;

          default:
            break;
        }

        mostrarAlerta(
          "error",
          mensaje
        );
      } finally {
        setIsSubmitting(
          false
        );
      }
    };

  // ==========================================================
  // ACTIVAR / DESACTIVAR
  // ==========================================================

  const toggleActivo =
    async (
      userId,
      nextValue
    ) => {
      if (!puedeAdministrar) {
        return;
      }

      const target =
        usuarios.find(
          (u) =>
            u.id === userId
        );

      if (!target) {
        return;
      }

      const esSelf =
        target.id ===
        adminRef.current.uid;

      if (
        esSelf &&
        nextValue === false
      ) {
        mostrarAlerta(
          "error",
          "No puedes desactivarte a ti mismo."
        );

        return;
      }

      if (
        target.emp_id !==
        empresaActual?.id
      ) {
        mostrarAlerta(
          "error",
          "No puedes modificar usuarios de otra empresa."
        );

        return;
      }

      if (
        esAdminHC &&
        !esRolHC(target.rol)
      ) {
        mostrarAlerta(
          "error",
          "No puedes administrar usuarios de Servicio Técnico."
        );

        return;
      }

      if (
        esAdminST &&
        !esRolST(target.rol)
      ) {
        mostrarAlerta(
          "error",
          "No puedes administrar usuarios HomeCare."
        );

        return;
      }

      setSavingById(
        (prev) => ({
          ...prev,
          [userId]: true,
        })
      );

      try {
        // ----------------------------------------------------
        // RELECTURA FRESCA DEL USUARIO
        // ----------------------------------------------------

        const usuarioRef =
          doc(
            db,
            "usuarios",
            userId
          );

        const snapUsuario =
          await getDoc(
            usuarioRef
          );

        if (!snapUsuario.exists()) {
          throw new Error(
            "El usuario ya no existe."
          );
        }

        const actualFirestore = {
          id:
            snapUsuario.id,
          ...snapUsuario.data(),
        };

        if (
          actualFirestore.emp_id !==
          empresaActual?.id
        ) {
          throw new Error(
            "El usuario ya no pertenece a esta empresa."
          );
        }

        if (
          esAdminHC &&
          !esRolHC(
            actualFirestore.rol
          )
        ) {
          throw new Error(
            "El usuario ya no pertenece al dominio HomeCare."
          );
        }

        if (
          esAdminST &&
          !esRolST(
            actualFirestore.rol
          )
        ) {
          throw new Error(
            "El usuario ya no pertenece al dominio Servicio Técnico."
          );
        }

        // ----------------------------------------------------
        // REACTIVACIÓN: PLAN
        // ----------------------------------------------------

        if (
          nextValue &&
          actualFirestore.activo ===
            false
        ) {
          const qUsuariosEmpresa =
            query(
              collection(
                db,
                "usuarios"
              ),
              where(
                "emp_id",
                "==",
                empresaActual.id
              )
            );

          const snap =
            await getDocs(
              qUsuariosEmpresa
            );

          const activos =
            snap.docs
              .map(
                (d) =>
                  d.data()
              )
              .filter(
                (u) =>
                  u.activo !==
                    false &&
                  u.rol !==
                    "DADMIN" &&
                  u.rol !==
                    "JADMIN"
              ).length;

          if (
            maxUsers &&
            activos >= maxUsers
          ) {
            throw new Error(
              `No se puede activar el usuario. El plan permite máximo ${maxUsers} usuario(s) activos.`
            );
          }
        }

        const batch =
          writeBatch(db);

        batch.update(
          usuarioRef,
          {
            activo:
              nextValue,

            user_mod:
              adminRef.current.email,

            fecha_mod:
              serverTimestamp(),
          }
        );

        const auditoriaRef =
          doc(
            collection(
              db,
              "auditoria_usuarios"
            )
          );

        batch.set(
          auditoriaRef,
          {
            accion:
              nextValue
                ? "ACTIVAR_USUARIO"
                : "DESACTIVAR_USUARIO",

            modulo:
              "CONFIGURACION",

            dominio:
              dominioAdministrador,

            entidad:
              "USUARIO",

            emp_id:
              actualFirestore.emp_id,

            empresa:
              actualFirestore.empresa ||
              empresaActual?.empresa ||
              "",

            objetivo_id:
              actualFirestore.id,

            objetivo_descripcion:
              actualFirestore.correo ||
              "",

            rol_objetivo:
              actualFirestore.rol ||
              "",

            ejecutado_por_uid:
              adminRef.current.uid,

            ejecutado_por_correo:
              adminRef.current.email,

            ejecutado_por_rol:
              adminRef.current.rol,

            datos_anteriores: {
              activo:
                actualFirestore.activo !==
                false,
            },

            datos_nuevos: {
              activo:
                nextValue,
            },

            resultado:
              "OK",

            fecha:
              serverTimestamp(),
          }
        );

        await batch.commit();

        setUsuarios(
          (prev) =>
            prev.map(
              (u) =>
                u.id ===
                userId
                  ? {
                      ...u,
                      activo:
                        nextValue,
                    }
                  : u
            )
        );

        mostrarAlerta(
          "exito",
          `Usuario ${
            nextValue
              ? "activado"
              : "desactivado"
          } correctamente.`
        );
      } catch (error) {
        console.error(
          "Error actualizando usuario:",
          error
        );

        mostrarAlerta(
          "error",
          error?.message ||
            "No se pudo actualizar el usuario."
        );
      } finally {
        setSavingById(
          (prev) => ({
            ...prev,
            [userId]: false,
          })
        );
      }
    };

  // ==========================================================
  // ABRIR CAMBIO DE ROL
  // ==========================================================

  const abrirCambioRol =
    (item) => {
      cambiarEstadoAlerta(false);

      if (!puedeAdministrar) {
        return;
      }

      if (!item?.id) {
        return;
      }

      if (
        item.id ===
        adminRef.current.uid
      ) {
        mostrarAlerta(
          "error",
          "No puedes cambiar tu propio rol."
        );

        return;
      }

      if (
        item.emp_id !==
        empresaActual?.id
      ) {
        mostrarAlerta(
          "error",
          "No puedes modificar usuarios de otra empresa."
        );

        return;
      }

      if (
        esAdminHC &&
        !esRolHC(item.rol)
      ) {
        mostrarAlerta(
          "error",
          "No puedes modificar el rol de un usuario de Servicio Técnico."
        );

        return;
      }

      if (
        esAdminST &&
        !esRolST(item.rol)
      ) {
        mostrarAlerta(
          "error",
          "No puedes modificar el rol de un usuario HomeCare."
        );

        return;
      }

      setUsuarioCambioRol(
        item
      );

      setNuevoRol(
        item.rol || ""
      );

      setModalRolOpen(
        true
      );
    };

  // ==========================================================
  // CERRAR CAMBIO DE ROL
  // ==========================================================

  const cerrarCambioRol = () => {
    if (guardandoRol) {
      return;
    }

    setModalRolOpen(false);

    setUsuarioCambioRol(
      null
    );

    setNuevoRol("");
  };

  // ==========================================================
  // CONFIRMAR CAMBIO DE ROL
  // ==========================================================

  const confirmarCambioRol =
    async () => {
      if (
        guardandoRol ||
        !usuarioCambioRol?.id
      ) {
        return;
      }

      cambiarEstadoAlerta(false);

      if (!puedeAdministrar) {
        mostrarAlerta(
          "error",
          "Tu perfil no puede modificar roles."
        );

        return;
      }

      if (
        usuarioCambioRol.id ===
        adminRef.current.uid
      ) {
        mostrarAlerta(
          "error",
          "No puedes cambiar tu propio rol."
        );

        cerrarCambioRol();
        return;
      }

      if (!nuevoRol) {
        mostrarAlerta(
          "error",
          "Debes seleccionar un rol."
        );

        return;
      }

      if (
        !rolPermitidoParaAdministrador(
          nuevoRol
        )
      ) {
        mostrarAlerta(
          "error",
          "El rol seleccionado no pertenece al dominio que administras."
        );

        return;
      }

      setGuardandoRol(true);

      try {
        // ----------------------------------------------------
        // LECTURA FRESCA
        // ----------------------------------------------------

        const usuarioRef =
          doc(
            db,
            "usuarios",
            usuarioCambioRol.id
          );

        const usuarioSnap =
          await getDoc(
            usuarioRef
          );

        if (!usuarioSnap.exists()) {
          throw new Error(
            "El usuario ya no existe."
          );
        }

        const usuarioFirestore = {
          id:
            usuarioSnap.id,
          ...usuarioSnap.data(),
        };

        // ----------------------------------------------------
        // TENANT
        // ----------------------------------------------------

        if (
          usuarioFirestore.emp_id !==
          empresaActual?.id
        ) {
          throw new Error(
            "El usuario ya no pertenece a esta empresa."
          );
        }

        // ----------------------------------------------------
        // DOMINIO ORIGINAL
        // ----------------------------------------------------

        if (
          esAdminHC &&
          !esRolHC(
            usuarioFirestore.rol
          )
        ) {
          throw new Error(
            "El usuario ya no pertenece al dominio HomeCare."
          );
        }

        if (
          esAdminST &&
          !esRolST(
            usuarioFirestore.rol
          )
        ) {
          throw new Error(
            "El usuario ya no pertenece al dominio Servicio Técnico."
          );
        }

        // ----------------------------------------------------
        // DOMINIO DESTINO
        // ----------------------------------------------------

        if (
          !rolPermitidoParaAdministrador(
            nuevoRol
          )
        ) {
          throw new Error(
            "El nuevo rol no está permitido para este dominio."
          );
        }

        // ----------------------------------------------------
        // SELF
        // ----------------------------------------------------

        if (
          usuarioFirestore.id ===
          adminRef.current.uid
        ) {
          throw new Error(
            "No puedes cambiar tu propio rol."
          );
        }

        // ----------------------------------------------------
        // SIN CAMBIOS
        // ----------------------------------------------------

        if (
          usuarioFirestore.rol ===
          nuevoRol
        ) {
          cerrarCambioRol();

          mostrarAlerta(
            "info",
            "El usuario ya tiene el rol seleccionado."
          );

          return;
        }

        const rolAnterior =
          usuarioFirestore.rol;

        // ----------------------------------------------------
        // BATCH: USUARIO + AUDITORÍA
        // ----------------------------------------------------

        const batch =
          writeBatch(db);

        batch.update(
          usuarioRef,
          {
            rol:
              nuevoRol,

            user_mod:
              adminRef.current.email,

            fecha_mod:
              serverTimestamp(),
          }
        );

        const auditoriaRef =
          doc(
            collection(
              db,
              "auditoria_usuarios"
            )
          );

        batch.set(
          auditoriaRef,
          {
            accion:
              "CAMBIAR_ROL_USUARIO",

            modulo:
              "CONFIGURACION",

            dominio:
              dominioAdministrador,

            entidad:
              "USUARIO",

            emp_id:
              usuarioFirestore.emp_id,

            empresa:
              usuarioFirestore.empresa ||
              empresaActual?.empresa ||
              "",

            objetivo_id:
              usuarioFirestore.id,

            objetivo_descripcion:
              usuarioFirestore.correo ||
              "",

            rol_objetivo:
              nuevoRol,

            ejecutado_por_uid:
              adminRef.current.uid,

            ejecutado_por_correo:
              adminRef.current.email,

            ejecutado_por_rol:
              adminRef.current.rol,

            datos_anteriores: {
              rol:
                rolAnterior,
            },

            datos_nuevos: {
              rol:
                nuevoRol,
            },

            resultado:
              "OK",

            fecha:
              serverTimestamp(),
          }
        );

        await batch.commit();

        // ----------------------------------------------------
        // UI LOCAL
        // ----------------------------------------------------

        setUsuarios(
          (prev) =>
            prev.map(
              (u) =>
                u.id ===
                usuarioFirestore.id
                  ? {
                      ...u,
                      rol:
                        nuevoRol,
                    }
                  : u
            )
        );

        setModalRolOpen(
          false
        );

        setUsuarioCambioRol(
          null
        );

        setNuevoRol("");

        mostrarAlerta(
          "exito",
          `Rol actualizado de ${rolAnterior} a ${nuevoRol}.`
        );
      } catch (error) {
        console.error(
          "Error cambiando rol:",
          error
        );

        let mensaje =
          error?.message ||
          "No fue posible cambiar el rol del usuario.";

        if (
          error?.code ===
            "permission-denied" ||
          error?.code ===
            "firestore/permission-denied"
        ) {
          mensaje =
            "Las reglas de seguridad no permiten realizar este cambio de rol.";
        }

        mostrarAlerta(
          "error",
          mensaje
        );
      } finally {
        setGuardandoRol(false);
      }
    };

  // ==========================================================
  // BUSCADOR
  // ==========================================================

  const q =
    useMemo(
      () =>
        String(
          buscadorDebounced ||
          ""
        )
          .trim()
          .toLowerCase(),
      [buscadorDebounced]
    );

  const usuariosFiltrados =
    useMemo(() => {
      if (!q) {
        return usuariosDominio;
      }

      return usuariosDominio.filter(
        (u) => {
          const texto = [
            u.correo,
            u.nombre,
            u.apellido,
            u.empresa,
            u.rol,
            u.activo !== false
              ? "activo"
              : "inactivo",
            obtenerDominioRol(
              u.rol
            ),
          ]
            .join(" ")
            .toLowerCase();

          return texto.includes(
            q
          );
        }
      );
    }, [
      usuariosDominio,
      q,
    ]);

  // ==========================================================
  // EXCEL
  // ==========================================================

  const ExportarXls = () => {
    if (
      usuariosFiltrados.length ===
      0
    ) {
      return;
    }

    const rows =
      usuariosFiltrados.map(
        (u) => ({
          empresa:
            u.empresa || "",

          dominio:
            obtenerDominioRol(
              u.rol
            ),

          nombre:
            u.nombre || "",

          apellido:
            u.apellido || "",

          correo:
            u.correo || "",

          rol:
            u.rol || "",

          activo:
            u.activo !== false
              ? "SI"
              : "NO",
        })
      );

    const columnsToShow = [
      "empresa",
      "dominio",
      "nombre",
      "apellido",
      "correo",
      "rol",
      "activo",
    ];

    const excelBlob =
      ExportarExcel(
        rows,
        columnsToShow
      );

    const excelURL =
      URL.createObjectURL(
        excelBlob
      );

    const downloadLink =
      document.createElement(
        "a"
      );

    downloadLink.href =
      excelURL;

    downloadLink.download =
      "usuariosRoles.xlsx";

    downloadLink.click();

    setTimeout(
      () =>
        URL.revokeObjectURL(
          excelURL
        ),
      2000
    );
  };

  // ==========================================================
  // KPIs
  // ==========================================================

  const totalVisible =
    usuariosDominio.length;

  const activosVisible =
    usuariosDominio.filter(
      (u) =>
        u.activo !== false
    ).length;

  const inactivosVisible =
    totalVisible -
    activosVisible;

  // ==========================================================
  // OPTIONS
  // ==========================================================

  const empresaOptions =
    useMemo(() => {
      return empresas.map(
        (e) => ({
          key:
            e.id,

          text:
            e.empresa ||
            e.id,

          value:
            e.id,
        })
      );
    }, [empresas]);

  const dominioOptions = [
    {
      key: "TODOS",
      text: "Todos",
      value:
        DOMINIOS.TODOS,
    },
    {
      key: "HC",
      text: "HomeCare",
      value:
        DOMINIOS.HC,
    },
    {
      key: "ST",
      text:
        "Servicio Técnico",
      value:
        DOMINIOS.ST,
    },
  ];

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div
      style={{
        minHeight:
          "100vh",

        background:
          "#f8fafc",

        padding:
          "26px 0 40px",
      }}
    >
      <Container
        style={{
          maxWidth: 1400,
        }}
      >
        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "center",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 20,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems:
                "center",
              gap: 14,
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 9,
                background:
                  "#e6f7f8",
                display: "flex",
                justifyContent:
                  "center",
                alignItems:
                  "center",
              }}
            >
              <Icon
                name="users"
                size="large"
                style={{
                  color:
                    "#0d9da5",
                  margin: 0,
                }}
              />
            </div>

            <div>
              <Header
                as="h2"
                style={{
                  margin: 0,
                  color:
                    "#172b4d",
                }}
              >
                {tituloPagina}

                <Header.Subheader
                  style={{
                    marginTop: 5,
                  }}
                >
                  {subtituloPagina}
                </Header.Subheader>
              </Header>
            </div>
          </div>

          <Button
            color="green"
            type="button"
            onClick={ExportarXls}
            disabled={
              usuariosFiltrados.length ===
              0
            }
          >
            <Icon name="file excel" />
            Exportar Excel
          </Button>
        </div>

        {/* ================================================= */}
        {/* EMPRESA / DOMINIO */}
        {/* ================================================= */}

        <Segment
          style={{
            border:
              "1px solid #e5e9ef",
            boxShadow:
              "0 1px 3px rgba(16,24,40,.05)",
            borderRadius: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems:
                "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <div>
              <Header
                as="h4"
                style={{
                  marginBottom: 4,
                  color:
                    "#253858",
                }}
              >
                <Icon
                  name="building outline"
                  color="blue"
                />

                {empresaActual?.empresa ||
                  "Empresa"}
              </Header>

              <span
                style={{
                  color:
                    "#6b778c",
                }}
              >
                Perfil conectado:{" "}
                <strong>
                  {rolActual || "-"}
                </strong>
              </span>
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems:
                  "center",
                flexWrap: "wrap",
              }}
            >
              {esAdminHC && (
                <Label
                  color="blue"
                  size="large"
                >
                  <Icon name="home" />
                  HOMECARE
                </Label>
              )}

              {esAdminST && (
                <Label
                  color="orange"
                  size="large"
                >
                  <Icon name="wrench" />
                  SERVICIO TÉCNICO
                </Label>
              )}

              {esBackoffice && (
                <Label
                  color="violet"
                  size="large"
                >
                  <Icon name="shield" />
                  BACKOFFICE
                </Label>
              )}
            </div>
          </div>

          {esBackoffice && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "minmax(260px, 1fr) minmax(220px, 320px)",
                gap: 12,
                marginTop: 18,
              }}
            >
              <Dropdown
                fluid
                search
                selection
                placeholder="Seleccionar empresa"
                value={
                  empresaSeleccionadaId
                }
                options={
                  empresaOptions
                }
                onChange={(
                  e,
                  { value }
                ) =>
                  setEmpresaSeleccionadaId(
                    value
                  )
                }
              />

              <Dropdown
                fluid
                selection
                value={
                  filtroDominio
                }
                options={
                  dominioOptions
                }
                onChange={(
                  e,
                  { value }
                ) =>
                  setFiltroDominio(
                    value
                  )
                }
              />
            </div>
          )}
        </Segment>

        {/* ================================================= */}
        {/* KPIs */}
        {/* ================================================= */}

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <KpiUsuario
            titulo="USUARIOS"
            valor={totalVisible}
            color="#172b4d"
          />

          <KpiUsuario
            titulo="ACTIVOS"
            valor={activosVisible}
            color="#21ba45"
          />

          <KpiUsuario
            titulo="INACTIVOS"
            valor={inactivosVisible}
            color="#db2828"
          />

          <KpiUsuario
            titulo="PLAN"
            valor={
              maxUsers
                ? `${usuariosActivosEmpresa.length} / ${maxUsers}`
                : usuariosActivosEmpresa.length
            }
            color={
              limiteUsuariosAlcanzado
                ? "#db2828"
                : "#0d9da5"
            }
          />
        </div>

        {/* ================================================= */}
        {/* CREAR USUARIO */}
        {/* ================================================= */}

        {puedeAdministrar && (
          <Segment
            style={{
              border:
                "1px solid #e5e9ef",

              boxShadow:
                "0 1px 3px rgba(16,24,40,.05)",

              borderRadius: 8,

              overflow:
                "visible",
            }}
          >
            <Header
              as="h4"
              style={{
                color:
                  "#253858",
              }}
            >
              <Icon
                name="user plus"
                style={{
                  color:
                    "#0d9da5",
                }}
              />

              Crear usuario

              <Header.Subheader>
                {esAdminHC
                  ? "Solo se pueden crear roles HomeCare."
                  : "Solo se pueden crear roles de Servicio Técnico."}
              </Header.Subheader>
            </Header>

            {limiteUsuariosAlcanzado && (
              <Message warning>
                <Message.Header>
                  Límite del plan alcanzado
                </Message.Header>

                No es posible crear más usuarios activos para esta empresa.
              </Message>
            )}

            <Form
              onSubmit={handleSubmit}
              loading={
                loadingInicial
              }
            >
              <Form.Group widths="equal">
                <Form.Input
                  fluid
                  label="Nombre"
                  placeholder="Nombre"
                  name="nombre"
                  value={nombre}
                  onChange={
                    handleChange
                  }
                  icon="user"
                  iconPosition="left"
                  disabled={
                    isSubmitting ||
                    limiteUsuariosAlcanzado
                  }
                />

                <Form.Input
                  fluid
                  label="Apellido"
                  placeholder="Apellido"
                  name="apellido"
                  value={apellido}
                  onChange={
                    handleChange
                  }
                  icon="user outline"
                  iconPosition="left"
                  disabled={
                    isSubmitting ||
                    limiteUsuariosAlcanzado
                  }
                />
              </Form.Group>

              <Form.Input
                fluid
                label="Correo electrónico"
                type="email"
                placeholder="usuario@empresa.cl"
                name="email"
                value={correo}
                onChange={
                  handleChange
                }
                icon="mail"
                iconPosition="left"
                disabled={
                  isSubmitting ||
                  limiteUsuariosAlcanzado
                }
              />

              <Form.Group widths="equal">
                <Form.Input
                  fluid
                  label="Contraseña"
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  name="password"
                  value={pass}
                  onChange={
                    handleChange
                  }
                  icon="key"
                  iconPosition="left"
                  disabled={
                    isSubmitting ||
                    limiteUsuariosAlcanzado
                  }
                />

                <Form.Input
                  fluid
                  label="Repetir contraseña"
                  type="password"
                  placeholder="Repetir contraseña"
                  name="password2"
                  value={pass2}
                  onChange={
                    handleChange
                  }
                  icon="key"
                  iconPosition="left"
                  disabled={
                    isSubmitting ||
                    limiteUsuariosAlcanzado
                  }
                />
              </Form.Group>

              <Form.Group widths="equal">
                <Form.Field>
                  <label>
                    Empresa
                  </label>

                  <Input
                    fluid
                    readOnly
                    value={
                      empresaActual?.empresa ||
                      ""
                    }
                    icon="building"
                  />
                </Form.Field>

                <Form.Field>
                  <label>
                    Rol
                  </label>

                  <Dropdown
                    fluid
                    search
                    selection
                    placeholder="Selecciona Rol"
                    value={rol}
                    onChange={(
                      e,
                      { value }
                    ) =>
                      setRol(value)
                    }
                    disabled={
                      isSubmitting ||
                      limiteUsuariosAlcanzado
                    }
                    options={
                      rolesDisponibles
                    }
                  />
                </Form.Field>
              </Form.Group>

              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "flex-end",
                  marginTop: 12,
                }}
              >
                <Button
                  color="teal"
                  type="submit"
                  loading={
                    isSubmitting
                  }
                  disabled={
                    isSubmitting ||
                    loadingInicial ||
                    limiteUsuariosAlcanzado
                  }
                >
                  <Icon name="save" />
                  Guardar usuario
                </Button>
              </div>
            </Form>
          </Segment>
        )}

        {esBackoffice && (
          <Message info>
            <Message.Header>
              Administración global
            </Message.Header>

            Desde Configuración puedes consultar usuarios HomeCare y Servicio Técnico. Las operaciones globales continúan centralizadas en BackOffice → Usuarios de la Plataforma.
          </Message>
        )}

        {!puedeAdministrar &&
          !esBackoffice && (
            <Message warning>
              Tu perfil no posee permisos para administrar usuarios.
            </Message>
          )}

        {/* ================================================= */}
        {/* LISTADO */}
        {/* ================================================= */}

        <Segment
          style={{
            border:
              "1px solid #e5e9ef",
            boxShadow:
              "0 1px 3px rgba(16,24,40,.05)",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems:
                "center",
              gap: 12,
              flexWrap: "wrap",
              paddingBottom: 14,
            }}
          >
            <Header
              as="h4"
              style={{
                margin: 0,
                color:
                  "#253858",
              }}
            >
              <Icon
                name="list alternate outline"
                color="blue"
              />

              Listado de usuarios
            </Header>

            <Input
              style={{
                width: 350,
                maxWidth: "100%",
              }}
              placeholder="Buscar nombre, correo o rol..."
              icon="search"
              value={buscador}
              onChange={(e) =>
                setBuscador(
                  e.target.value
                )
              }
            />
          </div>

          <div
            style={{
              overflowX: "auto",
            }}
          >
            <Table
              compact
              unstackable
              selectable
              style={{
                minWidth: 1000,
                margin: 0,
              }}
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell
                    style={{
                      position:
                        "sticky",
                      top: 0,
                      zIndex: 2,
                      background:
                        "#f8fafc",
                    }}
                  >
                    N°
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={{
                      position:
                        "sticky",
                      top: 0,
                      zIndex: 2,
                      background:
                        "#f8fafc",
                    }}
                  >
                    Usuario
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={{
                      position:
                        "sticky",
                      top: 0,
                      zIndex: 2,
                      background:
                        "#f8fafc",
                    }}
                  >
                    Correo
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={{
                      position:
                        "sticky",
                      top: 0,
                      zIndex: 2,
                      background:
                        "#f8fafc",
                    }}
                  >
                    Dominio
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={{
                      position:
                        "sticky",
                      top: 0,
                      zIndex: 2,
                      background:
                        "#f8fafc",
                    }}
                  >
                    Rol
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    textAlign="center"
                    style={{
                      position:
                        "sticky",
                      top: 0,
                      zIndex: 2,
                      background:
                        "#f8fafc",
                    }}
                  >
                    Estado
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    textAlign="center"
                    style={{
                      position:
                        "sticky",
                      top: 0,
                      zIndex: 2,
                      background:
                        "#f8fafc",
                    }}
                  >
                    Acciones
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {usuariosFiltrados.map(
                  (
                    item,
                    index
                  ) => {
                    const esMismoUsuario =
                      item.id ===
                      adminRef.current.uid;

                    const guardando =
                      Boolean(
                        savingById[
                          item.id
                        ]
                      );

                    return (
                      <Table.Row
                        key={item.id}
                        active={
                          selectedUserId ===
                          item.id
                        }
                        onClick={() =>
                          setSelectedUserId(
                            item.id
                          )
                        }
                      >
                        <Table.Cell>
                          {index + 1}
                        </Table.Cell>

                        <Table.Cell>
                          <strong>
                            {item.nombre ||
                              ""}{" "}
                            {item.apellido ||
                              ""}
                          </strong>

                          {esMismoUsuario && (
                            <Label
                              size="mini"
                              basic
                              color="blue"
                              style={{
                                marginLeft: 7,
                              }}
                            >
                              TÚ
                            </Label>
                          )}
                        </Table.Cell>

                        <Table.Cell>
                          {item.correo ||
                            ""}
                        </Table.Cell>

                        <Table.Cell>
                          {esRolHC(
                            item.rol
                          ) ? (
                            <Label
                              basic
                              color="blue"
                            >
                              HomeCare
                            </Label>
                          ) : (
                            <Label
                              basic
                              color="orange"
                            >
                              Servicio Técnico
                            </Label>
                          )}
                        </Table.Cell>

                        <Table.Cell>
                          <Label basic>
                            {item.rol}
                          </Label>
                        </Table.Cell>

                        <Table.Cell
                          textAlign="center"
                        >
                          {item.activo !==
                          false ? (
                            <Label
                              color="green"
                              basic
                            >
                              ACTIVO
                            </Label>
                          ) : (
                            <Label
                              color="red"
                              basic
                            >
                              INACTIVO
                            </Label>
                          )}
                        </Table.Cell>

                        <Table.Cell
                          textAlign="center"
                          onClick={(e) =>
                            e.stopPropagation()
                          }
                        >
                          {puedeAdministrar ? (
                            <div
                              style={{
                                display:
                                  "flex",
                                alignItems:
                                  "center",
                                justifyContent:
                                  "center",
                                gap: 10,
                              }}
                            >
                              <Button
                                type="button"
                                icon
                                size="mini"
                                disabled={
                                  guardando ||
                                  esMismoUsuario
                                }
                                title={
                                  esMismoUsuario
                                    ? "No puedes cambiar tu propio rol"
                                    : "Cambiar rol"
                                }
                                onClick={() =>
                                  abrirCambioRol(
                                    item
                                  )
                                }
                                style={{
                                  background:
                                    esMismoUsuario
                                      ? "#f4f5f7"
                                      : "#e8f3fb",

                                  color:
                                    esMismoUsuario
                                      ? "#a5adba"
                                      : "#2185d0",

                                  border:
                                    "1px solid #dfe5ec",

                                  boxShadow:
                                    "none",
                                }}
                              >
                                <Icon
                                  name="edit outline"
                                  style={{
                                    margin: 0,
                                  }}
                                />
                              </Button>

                              <Checkbox
                                toggle
                                checked={
                                  item.activo !==
                                  false
                                }
                                disabled={
                                  guardando
                                }
                                title={
                                  esMismoUsuario
                                    ? "No puedes desactivarte a ti mismo"
                                    : item.activo !== false
                                      ? "Desactivar usuario"
                                      : "Activar usuario"
                                }
                                onChange={(
                                  e,
                                  data
                                ) =>
                                  toggleActivo(
                                    item.id,
                                    data.checked
                                  )
                                }
                              />
                            </div>
                          ) : (
                            <Icon
                              name="eye"
                              color="grey"
                              title="Solo lectura"
                            />
                          )}
                        </Table.Cell>
                      </Table.Row>
                    );
                  }
                )}

                {usuariosFiltrados.length ===
                  0 && (
                  <Table.Row>
                    <Table.Cell
                      colSpan="7"
                      textAlign="center"
                      style={{
                        padding: 30,
                        color:
                          "#6b778c",
                      }}
                    >
                      <Icon
                        name="users"
                        size="large"
                      />

                      <div
                        style={{
                          marginTop: 8,
                        }}
                      >
                        No se encontraron usuarios para este dominio.
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>
        </Segment>

        {/* ================================================= */}
        {/* MODAL CONFIRMAR CREACIÓN */}
        {/* ================================================= */}

        <Modal
          size="small"
          open={confirmOpen}
          onClose={() =>
            !isSubmitting &&
            setConfirmOpen(false)
          }
        >
          <Modal.Header>
            Confirmar creación de usuario
          </Modal.Header>

          <Modal.Content>
            <p>
              Se creará una cuenta de acceso para:
            </p>

            <div
              style={{
                lineHeight: 1.9,
              }}
            >
              <div>
                <b>Nombre:</b>{" "}
                {nombre} {apellido}
              </div>

              <div>
                <b>Correo:</b>{" "}
                {correo}
              </div>

              <div>
                <b>Empresa:</b>{" "}
                {empresaActual?.empresa}
              </div>

              <div>
                <b>Dominio:</b>{" "}
                {dominioAdministrador}
              </div>

              <div>
                <b>Rol:</b>{" "}
                {rol}
              </div>
            </div>

            <Message
              warning
              style={{
                marginTop: 15,
              }}
            >
              La cuenta se creará en Firebase Authentication y se registrará su trazabilidad en auditoría.
            </Message>
          </Modal.Content>

          <Modal.Actions>
            <Button
              disabled={
                isSubmitting
              }
              onClick={() =>
                setConfirmOpen(false)
              }
            >
              Cancelar
            </Button>

            <Button
              color="teal"
              loading={
                isSubmitting
              }
              disabled={
                isSubmitting
              }
              onClick={
                confirmCreateUser
              }
            >
              <Icon name="check" />
              Crear usuario
            </Button>
          </Modal.Actions>
        </Modal>

        {/* ================================================= */}
        {/* MODAL CAMBIAR ROL */}
        {/* ================================================= */}

        <Modal
          size="mini"
          open={modalRolOpen}
          onClose={
            cerrarCambioRol
          }
          closeOnDimmerClick={
            !guardandoRol
          }
          closeOnEscape={
            !guardandoRol
          }
          style={{
            width: 460,
            maxWidth: "94vw",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <Modal.Header
            style={{
              padding:
                "13px 16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems:
                  "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  background:
                    "#e8f3fb",
                  display: "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center",
                }}
              >
                <Icon
                  name="user settings"
                  style={{
                    margin: 0,
                    color:
                      "#2185d0",
                  }}
                />
              </div>

              <div>
                <div
                  style={{
                    color:
                      "#172b4d",
                    fontSize: 15,
                    fontWeight:
                      700,
                  }}
                >
                  Cambiar rol
                </div>

                <div
                  style={{
                    color:
                      "#8793a5",
                    fontSize: 11,
                    marginTop: 2,
                  }}
                >
                  {usuarioCambioRol?.correo ||
                    ""}
                </div>
              </div>
            </div>
          </Modal.Header>

          <Modal.Content
            style={{
              padding:
                "16px 18px",
            }}
          >
            <div
              style={{
                padding:
                  "9px 11px",
                background:
                  "#f8fafc",
                border:
                  "1px solid #e5e9ef",
                borderRadius: 6,
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight:
                    700,
                  color:
                    "#253858",
                }}
              >
                {usuarioCambioRol
                  ? `${usuarioCambioRol.nombre || ""} ${usuarioCambioRol.apellido || ""}`.trim()
                  : ""}
              </div>

              <div
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color:
                    "#6b778c",
                }}
              >
                Rol actual:{" "}
                <strong>
                  {usuarioCambioRol?.rol ||
                    "-"}
                </strong>
              </div>
            </div>

            <Form>
              <Form.Field>
                <label>
                  Nuevo rol
                </label>

                <Dropdown
                  fluid
                  selection
                  search
                  value={nuevoRol}
                  options={
                    rolesDisponibles
                  }
                  disabled={
                    guardandoRol
                  }
                  onChange={(
                    e,
                    { value }
                  ) =>
                    setNuevoRol(
                      value
                    )
                  }
                />
              </Form.Field>
            </Form>

            {nuevoRol &&
              usuarioCambioRol?.rol !==
                nuevoRol && (
                <Message
                  info
                  size="small"
                  style={{
                    boxShadow:
                      "none",
                    marginTop: 12,
                  }}
                >
                  <Icon name="info circle" />

                  Se cambiará el rol de{" "}
                  <strong>
                    {usuarioCambioRol?.rol}
                  </strong>{" "}
                  a{" "}
                  <strong>
                    {nuevoRol}
                  </strong>
                  .
                </Message>
              )}

            <div
              style={{
                color:
                  "#8793a5",
                fontSize: 10,
                marginTop: 10,
              }}
            >
              El cambio quedará registrado en auditoría.
            </div>
          </Modal.Content>

          <Modal.Actions
            style={{
              padding:
                "10px 12px",
              background:
                "#f8fafc",
              borderTop:
                "1px solid #e5e9ef",
            }}
          >
            <Button
              size="small"
              disabled={
                guardandoRol
              }
              onClick={
                cerrarCambioRol
              }
            >
              Cancelar
            </Button>

            <Button
              size="small"
              color="teal"
              loading={
                guardandoRol
              }
              disabled={
                guardandoRol ||
                !nuevoRol ||
                nuevoRol ===
                  usuarioCambioRol?.rol
              }
              onClick={
                confirmarCambioRol
              }
            >
              <Icon name="check" />
              Guardar cambio
            </Button>
          </Modal.Actions>
        </Modal>

        <Alerta
          tipo={alerta.tipo}
          mensaje={
            alerta.mensaje
          }
          estadoAlerta={
            estadoAlerta
          }
          cambiarEstadoAlerta={
            cambiarEstadoAlerta
          }
        />
      </Container>
    </div>
  );
};

// ============================================================
// KPI
// ============================================================

const KpiUsuario = ({
  titulo,
  valor,
  color,
}) => {
  return (
    <Segment
      style={{
        margin: 0,
        border:
          "1px solid #e5e9ef",
        borderRadius: 8,
        boxShadow:
          "0 1px 3px rgba(16,24,40,.03)",
      }}
    >
      <div
        style={{
          color:
            "#6b778c",
          fontSize: 12,
        }}
      >
        {titulo}
      </div>

      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          color,
        }}
      >
        {valor}
      </div>
    </Segment>
  );
};

export default RegistroUsuarios;